// ResultTable.js
import React from 'react';

const ResultTable = ({ allResults, arrearResults }) => {
  // Sort results by semester and then by code
  const sortedResults = [...allResults].sort((a, b) => {
    if (a.semester !== b.semester) {
      return a.semester - b.semester;
    }
    return a.code.localeCompare(b.code);
  });

  // Define column widths for consistency
  const columnWidths = {
    sem: 'w-16',
    code: 'w-24',
    subject: 'w-48',
    internal: 'w-28',
    external: 'w-28',
    total: 'w-20',
    max: 'w-20',
    grade: 'w-20',
    status: 'w-24'
  };

  return (
    <div className="mb-6">
      {/* Main Results Table - Single table with all data */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <div className="max-h-96 overflow-y-auto">
            <table className="min-w-full table-fixed">
              <thead className="bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] text-white sticky top-0 z-10">
                <tr>
                  <th className={`${columnWidths.sem} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Sem</th>
                  <th className={`${columnWidths.code} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Code</th>
                  <th className={`${columnWidths.subject} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Subject</th>
                  <th className={`${columnWidths.internal} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Internal</th>
                  <th className={`${columnWidths.external} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>External</th>
                  <th className={`${columnWidths.total} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Total</th>
                  <th className={`${columnWidths.max} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Max</th>
                  <th className={`${columnWidths.grade} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Grade</th>
                  <th className={`${columnWidths.status} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedResults.map((subject, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className={`${columnWidths.sem} px-4 py-3 text-center text-sm font-medium text-gray-900`}>
                      <span className="px-2 py-1 bg-[#0d4f5c]/10 text-[#0d4f5c] rounded-full text-xs">
                        {subject.semester}
                      </span>
                    </td>
                    <td className={`${columnWidths.code} px-4 py-3 text-center text-sm text-gray-900`}>{subject.code}</td>
                    <td className={`${columnWidths.subject} px-4 py-3 text-center text-sm text-gray-900 truncate`} title={subject.name}>{subject.name}</td>
                    <td className={`${columnWidths.internal} px-4 py-3 text-center text-sm text-gray-900`}>
                      {subject.internalScored}/{subject.internalMax}
                    </td>
                    <td className={`${columnWidths.external} px-4 py-3 text-center text-sm text-gray-900`}>
                      {subject.externalScored}/{subject.externalMax}
                    </td>
                    <td className={`${columnWidths.total} px-4 py-3 text-center text-sm font-medium`}>
                      <span className={`${
                        subject.totalScored >= 50 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {subject.totalScored}
                      </span>
                    </td>
                    <td className={`${columnWidths.max} px-4 py-3 text-center text-sm text-gray-900`}>{subject.totalMax}</td>
                    <td className={`${columnWidths.grade} px-4 py-3 text-center`}>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        subject.grade === 'A' || subject.grade === 'A+' ? 'bg-green-100 text-green-800' :
                        subject.grade === 'B' || subject.grade === 'B+' ? 'bg-blue-100 text-blue-800' :
                        subject.grade === 'C' ? 'bg-yellow-100 text-yellow-800' :
                        subject.grade === 'D' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {subject.grade}
                      </span>
                    </td>
                    <td className={`${columnWidths.status} px-4 py-3 text-center`}>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        subject.pass === 'P' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {subject.pass === 'P' ? 'Pass' : 'Fail'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Arrear/Backlog Subjects Section */}
      {arrearResults.length > 0 && (
        <div className="mt-8">
          <h3 className="text-xl font-semibold text-red-600 mb-3 flex items-center gap-2">
            <span className="w-1 h-6 bg-red-600 rounded-full"></span>
            Arrear/Backlog Subjects
          </h3>
          <div className="bg-white rounded-lg shadow-md border-2 border-red-200 overflow-hidden">
            <div className="overflow-x-auto">
              <div className="max-h-64 overflow-y-auto">
                <table className="min-w-full table-fixed">
                  <thead className="bg-gradient-to-r from-red-600 to-red-700 text-white sticky top-0 z-10">
                    <tr>
                      <th className={`${columnWidths.sem} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Sem</th>
                      <th className={`${columnWidths.code} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Code</th>
                      <th className={`${columnWidths.subject} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Subject</th>
                      <th className={`${columnWidths.internal} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Internal</th>
                      <th className={`${columnWidths.external} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>External</th>
                      <th className={`${columnWidths.total} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Total</th>
                      <th className={`${columnWidths.max} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Max</th>
                      <th className={`${columnWidths.grade} px-4 py-3 text-center text-xs font-medium uppercase tracking-wider`}>Grade</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {arrearResults.map((subject, index) => (
                      <tr key={index} className="hover:bg-red-50 transition-colors duration-150 bg-red-50/50">
                        <td className={`${columnWidths.sem} px-4 py-3 text-center text-sm font-medium text-gray-900`}>
                          <span className="px-2 py-1 bg-red-200 text-red-700 rounded-full text-xs">
                            {subject.semester}
                          </span>
                        </td>
                        <td className={`${columnWidths.code} px-4 py-3 text-center text-sm text-gray-900`}>{subject.code}</td>
                        <td className={`${columnWidths.subject} px-4 py-3 text-center text-sm text-gray-900 truncate`} title={subject.name}>{subject.name}</td>
                        <td className={`${columnWidths.internal} px-4 py-3 text-center text-sm text-gray-900`}>
                          {subject.internalScored}/{subject.internalMax}
                        </td>
                        <td className={`${columnWidths.external} px-4 py-3 text-center text-sm text-gray-900`}>
                          {subject.externalScored}/{subject.externalMax}
                        </td>
                        <td className={`${columnWidths.total} px-4 py-3 text-center text-sm font-medium text-red-600`}>
                          {subject.totalScored}
                        </td>
                        <td className={`${columnWidths.max} px-4 py-3 text-center text-sm text-gray-900`}>{subject.totalMax}</td>
                        <td className={`${columnWidths.grade} px-4 py-3 text-center`}>
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            {subject.grade}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultTable;